import ArrayPreviewHelper from '@/editor/entities/2d/arrayPreviewHelper';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import EventBus from '@/editor/utils/EventBus';
import { EArrayPlaceMode, IArrayGridData, IBoxData } from '@/editor/types';

interface IRangeLimit {
  positive: number;
  negative: number;
}

interface IArrayPreviewParams {
  data: IBoxData;
  mode: EArrayPlaceMode;
  xLimit?: IRangeLimit;
  onConfirm: () => void;
}

const MAX_ARRAY_COUNT = 500;

export enum EArrayPreviewEventName {
  NOTIFY_SPACING_UPDATE,
  NOTIFY_CONFIG_UPDATE,
  NOTIFY_GRID_UPDATE
}

export type TArrayPreviewEvent = {
  [EArrayPreviewEventName.NOTIFY_CONFIG_UPDATE]: () => void;
  [EArrayPreviewEventName.NOTIFY_SPACING_UPDATE]: () => void;
  [EArrayPreviewEventName.NOTIFY_GRID_UPDATE]: () => void;
};

export class ArrayPreviewManager {
  #zoom: number;
  #params: IArrayPreviewParams | null = null;
  #lastEndPosition: IPoint2d | null = null;
  #spacing: IPoint2d = { x: 0, y: 0 };
  #helper: ArrayPreviewHelper | null = null;
  #gridData: IArrayGridData | null = null;
  #confirmCallback: (() => void) | null = null;
  #event = new EventBus<TArrayPreviewEvent>();

  constructor() {
    editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._updateZoom);
    this.#zoom = globalStores.scene2dZoom;
  }

  private _updateZoom = (zoom: number) => {
    this.#zoom = zoom;
    if (this.#helper) {
      this.#helper.updateScale(zoom);
    }
  };

  public create(data: IArrayPreviewParams) {
    this.#params = data;
    this.#spacing = { x: 0, y: 0 };
    this.#event.emit(EArrayPreviewEventName.NOTIFY_CONFIG_UPDATE);
    if (!this.#helper) {
      this.#helper = new ArrayPreviewHelper(data);
      this.#helper.updateScale(this.#zoom);
      this.#confirmCallback = data.onConfirm;
    }
  }

  public update(_params: { mousePos: IPoint2d }) {
    if (this.#helper && this.#params) {
      this.#lastEndPosition = _params.mousePos;
      this.#updateGrid();
    }
  }

  #updateGrid() {
    if (this.#helper && this.#params && this.#lastEndPosition) {
      this.#gridData = this.#calculateDynamicArrayGrid();
      this.#event.emit(EArrayPreviewEventName.NOTIFY_GRID_UPDATE);
      if (this.#gridData) {
        this.#helper.update(this.#gridData);
      }
    }
  }

  public clear() {
    if (this.#helper) {
      this.#helper.dispose();
      this.#helper = null;
      this.#params = null;
      this.#gridData = null;
      this.#lastEndPosition = null;
      this.#event.emit(EArrayPreviewEventName.NOTIFY_CONFIG_UPDATE);
    }
  }

  #calculateDynamicArrayGrid(): IArrayGridData | null {
    const params = this.#params;
    const end = this.#lastEndPosition;
    if (!params || !end) {
      return null;
    }

    let alignedEnd = end;

    const dirX = Point2dUtils.rotateDirection({ x: 1, y: 0 }, params.data.rotation);
    const dirY = Point2dUtils.rotateDirection({ x: 0, y: 1 }, params.data.rotation);
    const endOffset = Point2dUtils.subtracted(end, params.data.center);

    const projX = Point2dUtils.projectScalar(endOffset, dirX);
    const projY = Point2dUtils.projectScalar(endOffset, dirY);

    const stepX = params.data.width + this.#spacing.x;
    const stepY = params.data.thickness + this.#spacing.y;

    let countX: number;
    if (params.xLimit) {
      const xLimitValue = projX >= 0 ? params.xLimit.positive : params.xLimit.negative;
      const clampedProjAbs = Math.min(Math.abs(projX), xLimitValue);
      countX = Math.max(Math.floor((clampedProjAbs + params.data.width / 2 + this.#spacing.x) / stepX), 1);

      // 根据 end 对齐到 x 轴上，并且不能超过 xLimit
      const clampedProjX = Math.max(Math.min(projX, params.xLimit.positive), -params.xLimit.negative);
      alignedEnd = Point2dUtils.added(params.data.center, {
        x: clampedProjX * dirX.x,
        y: clampedProjX * dirX.y
      });
    } else {
      countX = Math.max(Math.floor((Math.abs(projX) + params.data.width / 2 + this.#spacing.x) / stepX), 1);
    }

    countX = Math.min(MAX_ARRAY_COUNT, countX);

    const countY =
      params.mode === EArrayPlaceMode.Line
        ? 1
        : Math.max(Math.floor((Math.abs(projY) + params.data.thickness / 2 + this.#spacing.y) / stepY), 1);

    const signX = projX >= 0 ? 1 : -1;
    const signY = projY >= 0 ? 1 : -1;

    const actualStepX = stepX * signX;
    const actualStepY = stepY * signY;
    const stepXVector = {
      x: actualStepX * dirX.x,
      y: actualStepX * dirX.y
    };
    const stepYVector = {
      x: actualStepY * dirY.x,
      y: actualStepY * dirY.y
    };

    return {
      end: alignedEnd,
      countX,
      // 如果超出最大数量限制，优先保证X方向的数量，并限制Y方向的数量
      countY: countX * countY > MAX_ARRAY_COUNT ? Math.max(Math.floor(MAX_ARRAY_COUNT / countX), 1) : countY,
      stepX: stepXVector,
      stepY: stepYVector,
      spacing: { ...this.#spacing }
    };
  }

  public confirm() {
    if (this.#confirmCallback) {
      this.#confirmCallback();
    }
  }

  public getSpacing = () => {
    return this.#spacing;
  };

  public setSpacing = (spacing: IPoint2d) => {
    this.#spacing = spacing;
    this.#updateGrid();
    this.#event.emit(EArrayPreviewEventName.NOTIFY_SPACING_UPDATE);
  };

  public getMode = () => {
    return this.#params?.mode;
  };

  public getGridData = () => {
    return this.#gridData;
  };

  public addListener = (eventName: EArrayPreviewEventName, callback: TArrayPreviewEvent[typeof eventName]) => {
    return this.#event.on(eventName, callback);
  };

  public removeListener = (eventName: EArrayPreviewEventName, callback: TArrayPreviewEvent[typeof eventName]) => {
    this.#event.off(eventName, callback);
  };
}
